// @flow

import * as R from 'ramda';

import { HttpService } from '~/services';
import {
  getRouteNavigationParamsWithFormattedDate,
  renameKeys,
} from '~/helpers';

export const resolvers = {
  Query: {
    archivedSelfConsumptionRewardHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?ArchivedSelfConsumptionRewardHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        PlatformName: 'platformName',
        MerchantName: 'merchantName',
        OrderID: 'orderId',
        DepositReturned: 'return',
        MembershipReward: 'membershipReward',
        ConsumptionReward: 'consumptionReward',
        TotalReward: 'totalReward',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          platformId: 'PlatformId',
          merchantName: 'MerchantName',
          orderId: 'OrderID',
          return: ['DepositReturnedFrom', 'DepositReturnedTo'],
          membershipReward: ['MembershipRewardFrom', 'MembershipRewardTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get self consumption list
      const {
        data: { SelfConsumptionRewardHistoryList, PageCount = 1 },
      } = await HttpService.client.get(
        '/Legacy/v1/MyWealth/GetSelfConsumptionRewardList',
        { params },
      );

      const items: ArchivedSelfConsumptionRewardHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            return: parseFloat,
            membershipReward: parseFloat,
          }),
          R.assoc('__typename', 'ArchivedSelfConsumptionRewardHistoryItem'),
        ),
        SelfConsumptionRewardHistoryList,
      );

      const selfConsumptionRewardList = {
        data: items,
        pages: PageCount,
        __typename: 'ArchivedSelfConsumptionRewardHistory',
      };

      cache.writeData({
        data: { selfConsumptionRewardList },
        variables: props,
      });

      return selfConsumptionRewardList;
    },
    archivedTeamConsumptionRewardHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?ArchivedTeamConsumptionRewardHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        PlatformName: 'platformName',
        MerchantName: 'merchantName',
        OrderID: 'orderId',
        DepositReturned: 'return',
        MembershipReward: 'membershipReward',
        ConsumptionReward: 'consumptionReward',
        HonoraryCitizenReward: 'extraReward',
        TotalReward: 'totalReward',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          platformId: 'PlatformId',
          merchantName: 'MerchantName',
          orderId: 'OrderID',
          return: ['DepositReturnedFrom', 'DepositReturnedTo'],
          membershipReward: ['MembershipRewardFrom', 'MembershipRewardTo'],
          consumptionReward: ['ConsumptionRewardFrom', 'ConsumptionRewardTo'],
          extraReward: ['HonoraryCitizenRewardFrom', 'HonoraryCitizenRewardTo'],
          totalReward: ['TotalRewardFrom', 'TotalRewardTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get team consumption list
      const {
        data: { TeamConsumptionRewardHistoryList, PageCount = 1 },
      } = await HttpService.client.get(
        '/Legacy/v1/MyWealth/GetTeamConsumptionRewardList',
        { params },
      );

      const items: ArchivedTeamConsumptionRewardHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            return: parseFloat,
            membershipReward: parseFloat,
            consumptionReward: parseFloat,
            extraReward: parseFloat,
            totalReward: parseFloat,
          }),
          R.assoc('__typename', 'ArchivedTeamConsumptionRewardHistoryItem'),
        ),
        TeamConsumptionRewardHistoryList,
      );

      const teamConsumptionRewardList = {
        data: items,
        pages: PageCount,
        __typename: 'ArchivedTeamConsumptionRewardHistory',
      };

      cache.writeData({
        data: { teamConsumptionRewardList },
        variables: props,
      });

      return teamConsumptionRewardList;
    },
    archivedMerchantReferralRewardHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?ArchivedMerchantReferralRewardHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        PlatformName: 'platformName',
        Merchantname: 'merchantName',
        OrderID: 'orderId',
        MerchantReferralReward: 'totalReward',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          platformId: 'PlatformId',
          merchantName: 'Merchantname',
          orderId: 'OrderID',
          totalReward: [
            'MerchantReferralRewardFrom',
            'MerchantReferralRewardTo',
          ],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get merchant referral reward list
      const {
        data: { TeamConsumptionRewardHistoryList, PageCount = 1, GrandTotal },
      } = await HttpService.client.get(
        '/Legacy/v1/MyWealth/GetMerchantRewardList',
        {
          params,
        },
      );

      const items: ArchivedMerchantReferralRewardHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            totalReward: parseFloat,
          }),
          R.assoc('__typename', 'ArchivedMerchantReferralRewardHistoryItem'),
        ),
        TeamConsumptionRewardHistoryList,
      );

      const merchantReferralRewardHistory = {
        data: items,
        pages: PageCount,
        grandTotal: GrandTotal,
        __typename: 'ArchivedMerchantReferralRewardHistory',
      };

      cache.writeData({
        data: { merchantReferralRewardHistory },
        variables: props,
      });

      return merchantReferralRewardHistory;
    },
    archivedDepositRewardHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?ArchivedDepositRewardHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        DepositAmount: 'amount',
        DepositReturned: 'return',
        DepositReturnedReward: 'returnedReward',
        DepositOverflowReward: 'overflowReward',
        ExtraReward: 'extraReward',
        TotalReward: 'totalReward',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          amount: ['DepositAmountFrom', 'DepositAmountTo'],
          return: ['DepositReturnedFrom', 'DepositReturnedTo'],
          returnedReward: [
            'DepositReturnedRewardFrom',
            'DepositReturnedRewardTo',
          ],
          overflowReward: [
            'DepositOverflowRewardFrom',
            'DepositOverflowRewardTo',
          ],
          extraReward: ['ExtraRewardFrom', 'ExtraRewardTo'],
          totalReward: ['TotalRewardFrom', 'TotalRewardTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get deposit reward list
      const {
        data: { DepositRewardHistoryList, PageCount = 1, TotalDepositReward },
      } = await HttpService.client.get(
        '/Legacy/v1/MyWealth/GetDepositRewardList',
        {
          params,
        },
      );

      const items: ArchivedDepositRewardHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            amount: parseFloat,
            return: parseFloat,
            returnedReward: parseFloat,
            overflowReward: parseFloat,
            extraReward: parseFloat,
            totalReward: parseFloat,
          }),
          R.assoc('__typename', 'ArchivedDepositRewardHistoryItem'),
        ),
        DepositRewardHistoryList,
      );

      const depositRewardHistory = {
        data: items,
        pages: PageCount,
        totalDepositReward: TotalDepositReward,
        __typename: 'ArchivedDepositRewardHistory',
      };

      cache.writeData({
        data: { depositRewardHistory },
        variables: props,
      });

      return depositRewardHistory;
    },
  },
};
